import React, {
  useState, useEffect, useContext, useCallback,
} from 'react';
import cx from 'classnames';
import _ from 'lodash';
import {useRouter} from 'next/router';

import Button from '../../components/ButtonNew';
import {SearchIcon} from '../../components/svg';
import {CourseCarousel} from '../../components/LinkedData';
import {Chiclet, ChicletsContainer} from '../../components/Chiclets';
import CourseCard, {Placeholder} from './CourseCard';
import {CourseLessonsContext} from '../../components/CourseLessonsProvider';
import {CourseVisits} from '../../../../lib/constants/courses';
import {useAnalytics, useAppContext} from '../../hooks';
import {createURLWithParams} from '../../util';
import {getRecommendations} from '../../api/api';
import {Searcher} from '../../../../lib/fuzzySearch';

const DEFAULT_NUM_RESULTS = 10;

// tag should correspond to tag.id in the database
const ChicletOptions = [
  {chiclet: 'All', tag: null},
  {chiclet: 'PM', tag: 'pm'},
  {chiclet: 'EM', tag: 'em'},
  {chiclet: 'SWE', tag: 'swe'},
  {chiclet: 'Data Analytics', tag: 'data-analyst'},
  {chiclet: 'Data Engineering', tag: 'data-engineer'},
  {chiclet: 'Machine Learning', tag: 'ml-engineer'},
  {chiclet: 'Behavioral', tag: 'behavioral'},
  {chiclet: 'System Design', tag: 'system-design'},
  {chiclet: 'TPM', tag: 'tpm'},
  {chiclet: 'UX / Product Design', tag: 'product-designer'},
  {chiclet: 'Product Analytics', tag: 'product-analyst'},
  {chiclet: 'Data Science', tag: 'data-science'},
  {chiclet: 'SQL', tag: 'sql'},
  {chiclet: 'Amazon', tag: 'amazon'},
  {chiclet: 'Google', tag: 'google'},
  {chiclet: 'Meta (Facebook)', tag: 'facebook'},
  {chiclet: 'Solutions Architect', tag: 'solutions-architect'},
  {chiclet: 'BizOps', tag: 'bizops'},
];

const TextInput = ({className, ...otherProps}) => (
  <input
    className={cx('text-base rounded-lg shadow-sm border border-gray-300 px-3 py-2 w-full focus:outline-none focus:border-gray-500', className)}
    {...otherProps}
  />
);

const SearchBar = ({value, onChange}) => (
  <span className="relative">
    <TextInput
      placeholder="Search Courses"
      className="pl-10"
      value={value}
      onChange={onChange}
    />
    <SearchIcon className="absolute m-auto top-0 bottom-0 left-3 h-5 w-5 pointer-events-none" />
  </span>
);

const sortSearchResults = (coursesAndTracks, recommendations) => {
  const [recommended, rest] = _.partition(
    coursesAndTracks,
    ({item}) => recommendations?.includes(item?.slug),
  );

  const [tracks, courses] = _.partition(
    rest,
    ({item}) => item?.contentType === 'track',
  );

  return [...recommended, ...tracks, ...courses];
};

const CourseLibrary = ({
  tag, query, updateParams, tagMap,
}) => {
  const {currentUser} = useAppContext();
  const {courses, loading: providerLoading} = useContext(CourseLessonsContext);
  const [searchQuery, setSearchQuery] = useState(query);
  const [fuzzySearcher, setFuzzySearcher] = useState(null);
  const [filteredResults, setFilteredResults] = useState([]);
  const [recommendations, setRecommendations] = useState();
  const [loading, setLoading] = useState(true);
  const {tracker} = useAnalytics();
  const router = useRouter();

  // Set up fuse search
  useEffect(() => {
    if (!providerLoading) {
      const availableCourses = courses.filter((c) => c.availableInCatalog);
      const searcher = new Searcher(availableCourses, ['name', 'title', 'description', 'tags'], {
        threshold: 0.3,
      });
      setFuzzySearcher(searcher);
    }
  }, [providerLoading]);

  // Load recommended courses
  useEffect(() => {
    if (currentUser) {
      getRecommendations().then((recs) => {
        setRecommendations(recs.courses.map((rec) => rec.slug));
      });
    }
  }, [currentUser]);

  // Run a search for query or tag, or clear filtering
  useEffect(() => {
    if (fuzzySearcher) {
      const searchResults = fuzzySearcher.search(query || tagMap[tag] || tag);
      setFilteredResults(sortSearchResults(searchResults, recommendations));
      setLoading(false);
    }
  }, [fuzzySearcher, query, tag, tagMap, recommendations]);

  const completeSearch = useCallback(_.debounce(
    (value) => {
      updateParams({query: value, tag: null});
      tracker.courseListSearch({value});
    },
    500,
  ), [updateParams]);

  const handleSearchQueryChange = ({target: {value}}) => {
    setSearchQuery(value);
    completeSearch(value);
  };

  const handleChicletChange = (value) => {
    tracker.courseListClickChiclet({value});
    setSearchQuery('');
  };

  const clearQuery = () => {
    updateParams({query: null, tag: null});
    setSearchQuery('');
  };

  const isLoading = loading || providerLoading;

  const onClickCourse = (item) => (e) => {
    e.preventDefault();
    tracker.courseListClickCourse({
      courseID: item.slug,
      recommended: recommendations?.includes(item.slug),
    });
    router.push(`/courses/${item.slug}`);
  };

  return (
    <div>
      <CourseCarousel courses={_.map(filteredResults, 'item')} />
      <div className="flex flex-col md:flex-row mb-3 md:mb-4">
        <div className="w-full">
          <h1 className="text-2xl font-semibold mb-2">
            {tag in tagMap ? `${tagMap[tag]} Courses` : 'Course Library'}
          </h1>
          <span className="text-base font-normal text-gray-500">
            Explore all Exponent courses tailored for specific roles, companies, and skills.
          </span>
        </div>
        <div className="w-full mt-4 md:mt-1">
          <SearchBar value={searchQuery} onChange={handleSearchQueryChange} />
        </div>
      </div>
      <ChicletsContainer className="w-full mb-6">
        {ChicletOptions.map(({chiclet, tag: chicletTag}) => (
          <Chiclet
            name={chiclet}
            href={createURLWithParams('/courses', {tag: chicletTag})}
            selected={chicletTag === tag || (!tag && chiclet === 'All')}
            onClick={handleChicletChange}
            key={chiclet}
          />
        ))}
      </ChicletsContainer>
      <div className="w-full grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {isLoading && _.times(DEFAULT_NUM_RESULTS, () => <Placeholder />)}
        {filteredResults.map(({item}) => (
          <CourseCard
            name={item.shortTitle}
            href={`/courses/${item.slug}`}
            description={item.description}
            thumbnail={item.thumbnail}
            numLessons={item.lessons?.length}
            numCourses={item.courses?.length}
            numStudents={CourseVisits[item.slug]}
            onClick={onClickCourse(item)}
            badge={recommendations?.includes(item.slug) ? 'Recommended' : null}
            isPath={item.contentType === 'track'}
          />
        ))}
      </div>
      {!isLoading && !filteredResults.length && (
        <div className="mt-8 flex flex-col items-center">
          <span className="text-lg font-semibold text-gray-600">
            Sorry, there aren&rsquo;t any courses that match your search.
          </span>
          <Button outline onClick={clearQuery} className="mt-4 p-2">
            Clear filters
          </Button>
        </div>
      )}
    </div>
  );
};

export default CourseLibrary;
